// The dashboards' layout model. Everyone has one "My layout" per dashboard — their personal
// default, which is simply the system default until they first save. Named layouts are an extra
// for the few who juggle several, and never stand in the way of the one-click Save.

export interface LayoutSummary {
    id: string
    name: string | null
    isDefault: boolean
    sortOrder: number
}

/** What a save writes to: an existing personal layout (by id), or a new one (id null). */
export interface SaveTarget {
    id: string | null
    name: string
    isDefault: boolean
}

export const MY_LAYOUT_NAME = 'My layout'

export const displayName = (l: Pick<LayoutSummary, 'name'>) => l.name?.trim() || 'Untitled'

/** The user's own default for this dashboard — My layout — if they have saved one. */
export const findMyLayout = (layouts: LayoutSummary[]) => layouts.find(l => l.isDefault) ?? null

/**
 * The personal layout on screen, checked against the user's own list: the system layout's id, or
 * one left behind by a deleted layout, isn't in it and so is never written to.
 */
export const findOnScreen = (layouts: LayoutSummary[], currentLayoutId: string | null) =>
    (currentLayoutId ? layouts.find(l => l.id === currentLayoutId) : undefined) ?? null

export function findByName(layouts: LayoutSummary[], name: string) {
    const wanted = name.trim().toLocaleLowerCase()
    if (!wanted) return null
    return layouts.find(l => displayName(l).toLocaleLowerCase() === wanted) ?? null
}

/** `base`, or "base 2", "base 3"… when one of the layouts already has that name. */
export function uniqueName(base: string, layouts: LayoutSummary[]) {
    if (!findByName(layouts, base)) return base
    let n = 2
    while (findByName(layouts, `${base} ${n}`)) n++
    return `${base} ${n}`
}

export type SaveKind = 'current' | 'create-mine' | 'replace-mine'

/**
 * What a plain Save writes to: the layout on screen when it is one of the user's own. With the
 * system default on screen it is My layout instead — created if they have none, replaced if they do.
 */
export function planSave(layouts: LayoutSummary[], currentLayoutId: string | null): { kind: SaveKind; target: SaveTarget } {
    const onScreen = findOnScreen(layouts, currentLayoutId)
    if (onScreen) {
        return { kind: 'current', target: { id: onScreen.id, name: displayName(onScreen), isDefault: onScreen.isDefault } }
    }
    const mine = findMyLayout(layouts)
    if (mine) return { kind: 'replace-mine', target: { id: mine.id, name: displayName(mine), isDefault: true } }
    return { kind: 'create-mine', target: { id: null, name: uniqueName(MY_LAYOUT_NAME, layouts), isDefault: true } }
}

/** True when the system default was saved after the user's own copy was. */
export function isBehindSystem(layoutUpdatedAt: string | null, systemUpdatedAt: string | null) {
    if (!layoutUpdatedAt || !systemUpdatedAt) return false
    return Date.parse(systemUpdatedAt) > Date.parse(layoutUpdatedAt)
}

/** The API's reason — `{ "error": "…" }` or plain text — as something fit to show; else `fallback`. */
export function apiErrorMessage(e: unknown, fallback = 'The layout could not be saved. Please try again.') {
    const raw = (e instanceof Error ? e.message : String(e ?? '')).trim()
    try {
        const parsed = JSON.parse(raw)
        if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error
    } catch { /* not JSON */ }
    // An HTML error page (a proxy's 5xx) is no use to anyone reading a dialog.
    return raw && raw.length < 200 && !raw.startsWith('<') ? raw : fallback
}
